import React from "react";
import { Checkbox, Col, Divider, Modal, Row, Space } from "antd";
import { ICourtCase } from "../../model/CourtCase";
import { TTranslate } from "../../../core/service/locale/TranslationService";
import CaseDecisionSelectionGroup from "./CaseDecisionSelectionGroup";
import { ICasePhase } from "../../model/CasePhase";
import CaseLawDocumentCourtCaseDescription from "./CaseLawDocumentCourtCaseDescription";
import { ICaseDecision } from "../../../caseDecisions/model/CaseDecision";
import { TCourtCaseSelectionModel } from "../../presenter/CaseLawDocumentCreationPresenter";
import { TCourtCaseConnectionType } from "../../model/CourtCaseConnectionType";
import SelectInput from "../../../core/ui/components/SelectInput";
import { IGenericCodebookItem } from "../../../codebooks/model/GenericCodebookItem";
import AlertMessage from "../../../core/ui/components/AlertMessage";

export interface Props {
  courtCase?: ICourtCase;
  visible: boolean;
  translate: TTranslate;
  onHide(): void;
  onSetCourtCaseSelectionResult(value: TCourtCaseSelectionModel);
  courtCaseConnectionTypeList: TCourtCaseConnectionType[];
  form: any;
  decisionTypes: IGenericCodebookItem[];
}

export type TCourtPhaseSelectionModel = {
  phase: ICasePhase;
  caseDecision?: ICaseDecision;
  connectionTypeId: number;
};
export interface State {
  selectedCourtPhases?: TCourtPhaseSelectionModel[];
  selectedCaseDecision?: ICaseDecision;
  selectedConnectedDecisions?: { [courtCaseNumber: string]: ICaseDecision };
}
export default class CourtCaseEntryForm extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      selectedCourtPhases: []
    };
    this.onSubmit = this.onSubmit.bind(this);
    this.onSelectCasePhaseDecision = this.onSelectCasePhaseDecision.bind(this);
  }

  onSubmit() {
    let result = {
      courtCase: this.props.courtCase,
      caseDecision:
        this.state.selectedCaseDecision || this.props.courtCase?.decisions?.[0],
      relatedCases: (this.state.selectedCourtPhases || []).map((item) => {
        item.phase.courtCase.connectionTypeId = item.connectionTypeId;
        return {
          courtCase: {
            ...item.phase.courtCase,
            connectionTypeId: item.connectionTypeId
          },
          caseDecision: item?.caseDecision
        };
      })
    };
    this.props.onSetCourtCaseSelectionResult(result);
    this.props.onHide();
  }

  private onSelectCasePhaseDecision(phase: ICasePhase, value: ICaseDecision) {
    this.setState((prevState, props) => {
      let selectedPhases = prevState.selectedCourtPhases;
      let result = selectedPhases?.find(
        (item) => item.phase.courtCaseNumber === phase.courtCaseNumber
      );
      if (result) {
        result.caseDecision = value;
        let selectedConnectedDecision = this.state.selectedConnectedDecisions;
        if (selectedConnectedDecision) {
          selectedConnectedDecision[phase.courtCaseNumber] = value;
        }
        return {
          selectedCourtPhases: selectedPhases,
          selectedConnectedDecisions: selectedConnectedDecision
        };
      }
      return null;
    });
  }
  private onChangeConnectionType(id: number, courtCaseNumber) {
    let selectedCourtPhases = this.state.selectedCourtPhases;
    if (selectedCourtPhases) {
      let index = selectedCourtPhases.findIndex(
        (item) => item?.phase?.courtCaseNumber === courtCaseNumber
      );
      if (index !== -1) {
        selectedCourtPhases[index].connectionTypeId = id;
        this.setState({ selectedCourtPhases });
      }
    }
  }

  render() {
    const {
      courtCase,
      visible,
      translate,
      onHide,
      courtCaseConnectionTypeList,
      form,
      decisionTypes
    } = this.props;
    if (!courtCase) {
      return null;
    }
    let searchLink = `${window.location.origin}/search?ccn=${courtCase?.courtCaseNumber}`;
    return (
      <Modal
        title={`${courtCase?.courtCaseNumber}${translate(
          "editors.court_cases.entry_form.data"
        )}`}
        visible={visible}
        onOk={() => {
          form
            .validateFields()
            .then(() => this.onSubmit())
            .catch((result) => {
              let errors: any[] = result?.errorFields;
              let isDecisionDateMissing = errors.some(
                (item) => item.name?.indexOf("decisionDate") > -1
              );
              let isDecisionTypeIdMissing = errors.some(
                (item) => item.name?.indexOf("decisionTypeId") > -1
              );
              if (!isDecisionDateMissing && !isDecisionTypeIdMissing) {
                this.onSubmit();
              }
            });
        }}
        onCancel={onHide}
        width="60%"
      >
        {courtCase && (
          <Space direction="vertical" style={{ width: "100%" }}>
            {courtCase.hasCaseLawDocs && (
              <div style={{ marginLeft: 24 }}>
                <AlertMessage
                  closable={false}
                  title={translate(
                    "editors.court_cases.entry_form.info_alert_title"
                  )}
                  message={
                    <p>
                      {translate(
                        "editors.court_cases.entry_form.info_alert_message"
                      )}
                      <a
                        href={searchLink}
                        rel="noopener noreferrer"
                        target="_blank"
                        style={{ color: "blue", textDecoration: "underline" }}
                      >
                        {translate(
                          "editors.court_cases.entry_form.placeholder"
                        )}
                      </a>
                    </p>
                  }
                  type={"info"}
                />
              </div>
            )}
            <Divider orientation="left">
              {translate("editors.court_cases.entry_form.basic_data")}
            </Divider>
            <div style={{ marginLeft: 24 }}>
              <CaseLawDocumentCourtCaseDescription
                courtCase={courtCase}
                translate={translate}
                decisionTypes={decisionTypes}
              />
            </div>
            <Divider orientation="left" plain>
              {translate("editors.court_cases.entry_form.case_decisions")}
            </Divider>
            <div style={{ marginLeft: 24, marginBottom: 60 }}>
              <CaseDecisionSelectionGroup
                onSelectDecision={(val) =>
                  this.setState({
                    selectedCaseDecision: val
                  })
                }
                selectedCaseDecision={
                  this.state.selectedCaseDecision ?? courtCase.decisions?.[0]
                }
                courtCase={courtCase}
                translate={translate}
                hint={translate("editors.court_cases.entry_form.hint")}
                caseDecisions={courtCase.decisions}
                decisionTypes={decisionTypes}
              />
            </div>
            <a
              href={`/case-decisions/new/${courtCase.courtCaseNumber}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {translate("editors.court_cases.entry_form.get_more_decisions")}
            </a>
            {courtCase.phases?.length ? (
              <>
                <Divider orientation="left">
                  {translate("editors.court_cases.entry_form.connected_cases")}
                </Divider>
                <div>
                  {courtCase.phases?.map((phase, index) => {
                    if (phase.courtCase && phase.courtCase.decisions.length > 0)
                      return (
                        <div
                          key={`phase-item-${index}`}
                          style={{ marginLeft: 24, marginBottom: 12 }}
                        >
                          <Row>
                            <Col span={16}>
                              <Checkbox
                                style={{ marginTop: 4 }}
                                defaultChecked={false}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    let selectedPhases =
                                      this.state.selectedCourtPhases || [];
                                    let selectedConnectedDecision =
                                      this.state.selectedConnectedDecisions ||
                                      {};
                                    selectedConnectedDecision[
                                      phase.courtCaseNumber
                                    ] = phase?.courtCase.decisions?.[0];
                                    this.setState({
                                      selectedCourtPhases: [
                                        ...selectedPhases,
                                        {
                                          phase,
                                          caseDecision:
                                            phase.courtCase.decisions?.[0],
                                          connectionTypeId:
                                            courtCaseConnectionTypeList?.[0].id
                                        }
                                      ],
                                      selectedConnectedDecisions:
                                        selectedConnectedDecision
                                    });
                                  } else {
                                    this.setState({
                                      selectedCourtPhases:
                                        this.state.selectedCourtPhases?.filter(
                                          (item) =>
                                            item.phase.courtCaseNumber !==
                                            phase.courtCaseNumber
                                        )
                                    });
                                  }
                                }}
                              >
                                {phase.courtCaseNumber}
                              </Checkbox>
                            </Col>

                            <Col
                              span={8}
                              style={
                                this.state.selectedCourtPhases?.findIndex(
                                  (item) =>
                                    item.phase?.courtCaseNumber ===
                                    phase?.courtCaseNumber
                                ) === -1
                                  ? { display: "none" }
                                  : { textAlign: "right" }
                              }
                            >
                              <SelectInput
                                placeholder={translate(
                                  "editors.court_cases.entry_form.select_connection_type"
                                )}
                                allowClear
                                showSearch={true}
                                notFoundContent={translate("search.no_data")}
                                selectOptionKey={"id"}
                                selectOptionValue={"name"}
                                selectOptions={courtCaseConnectionTypeList}
                                defaultActiveFirstOption={true}
                                defaultValue={
                                  courtCaseConnectionTypeList?.[0].id
                                }
                                onChange={(value) => {
                                  this.onChangeConnectionType(
                                    value,
                                    phase.courtCaseNumber
                                  );
                                }}
                              />
                            </Col>
                          </Row>

                          {this.state.selectedCourtPhases?.some(
                            (item) =>
                              item.phase.courtCaseNumber ===
                                phase.courtCaseNumber &&
                              item?.phase?.courtCase?.decisions.length > 0
                          ) ? (
                            <div style={{ marginLeft: 25, marginTop: 12 }}>
                              <CaseLawDocumentCourtCaseDescription
                                translate={translate}
                                courtCase={phase.courtCase}
                                selectedDecision={
                                  this.state.selectedConnectedDecisions?.[
                                    phase.courtCaseNumber
                                  ]
                                }
                                decisionTypes={decisionTypes}
                                showDecisions
                                compact
                                onSelectDecision={(value) => {
                                  this.onSelectCasePhaseDecision(phase, value);
                                }}
                              />
                            </div>
                          ) : null}
                        </div>
                      );
                  })}
                </div>
              </>
            ) : null}
            <a
              href={`/case-decisions/new/${courtCase.courtCaseNumber}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {translate(
                "editors.court_cases.entry_form.get_more_connected_cases"
              )}
            </a>
          </Space>
        )}
      </Modal>
    );
  }
}
